import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface Props {
	/** The member's current age, used for the example. */
	currentAge?: number | null;
}

/**
 * The "i" next to the Current age label when the member's age is estimated.
 * Explains that the estimate goes up by 1 every 1 January.
 */
export default function EstimatedAgeInfoHint({ currentAge }: Props) {
	const { t } = useTranslation();
	const age = currentAge ?? 19;
	const year = dayjs().year();

	const content = (
		<Box sx={{ padding: '18px 8px 14px' }}>
			<Typography
				variant='subtitle1'
				sx={{
					fontWeight: 'bold',
					color: 'common.neutral20',
					padding: '0 16px 14px',
					borderBottom: '1px solid',
					borderColor: 'common.neutral90',
				}}
			>
				{t('memberAge.hintTitle')}
			</Typography>

			<Box sx={{ padding: '14px 16px 0' }}>
				<Typography variant='body2' sx={{ color: 'common.neutral30' }}>
					{t('memberAge.hintDescription')}
				</Typography>
			</Box>

			<Box
				sx={{
					margin: '12px 8px 0',
					padding: '14px 16px',
					borderRadius: '12px',
					backgroundColor: 'background.default',
				}}
			>
				<Typography
					variant='body1'
					sx={{ fontWeight: 'bold', color: 'common.neutral20' }}
				>
					{t('memberAge.hintExampleTitle')}
				</Typography>
				<Typography
					variant='body2'
					sx={{ color: 'common.neutral30', marginTop: '2px' }}
				>
					{t('memberAge.hintExample', {
						age,
						year,
						age1: age + 1,
						year1: year + 1,
						age2: age + 2,
						year2: year + 2,
					})}
				</Typography>
			</Box>

			<Box sx={{ padding: '12px 16px 0' }}>
				<Typography variant='body2' sx={{ color: 'common.neutral50' }}>
					{t('memberAge.hintNote')}
				</Typography>
			</Box>
		</Box>
	);

	return (
		<Tooltip
			title={content}
			arrow
			placement='bottom-start'
			enterDelay={200}
			leaveDelay={100}
			enterTouchDelay={0}
			leaveTouchDelay={15000}
			slotProps={{
				tooltip: {
					sx: {
						width: '440px',
						maxWidth: 'calc(100vw - 48px)',
						padding: 0,
						backgroundColor: 'common.white',
						color: 'common.neutral30',
						borderRadius: '16px',
						border: '1px solid',
						borderColor: 'common.neutral90',
						boxShadow: '0px 8px 24px rgba(33, 37, 41, 0.12)',
					},
				},
				arrow: {
					sx: {
						color: 'common.white',
						'&::before': {
							border: '1px solid',
							borderColor: 'common.neutral90',
						},
					},
				},
			}}
		>
			<IconButton
				size='small'
				disableRipple
				aria-label={t('memberAge.hintTitle')}
				sx={{
					padding: 0,
					marginLeft: '4px',
					color: 'common.neutral50',
					'&:hover': {
						color: 'common.neutral20',
						backgroundColor: 'transparent',
					},
				}}
			>
				<InfoOutlinedIcon sx={{ fontSize: '16px' }} />
			</IconButton>
		</Tooltip>
	);
}
